/**
 * JOShopHero — Custom header strip injected at the top of the cockpit /shop page
 * for Jardins d'Éole. Highlights "Offres du moment" + Click & Collect / Livraison.
 * Renders ONLY on /shop and only when universes_enabled.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flame, Truck, Store, ArrowRight, Tag } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { api } from "@/lib/api";

export default function JOShopHero() {
  const loc = useLocation();
  const { enabled, universe } = useUniverse();
  const [offers, setOffers] = useState([]);
  const isShopRoot = loc.pathname === "/shop" || loc.pathname === "/shop/";

  useEffect(() => {
    if (!enabled || !isShopRoot) return;
    let alive = true;
    api.get("/products?limit=24").then((r) => {
      if (!alive) return;
      const list = (r.data?.items || r.data || []).filter((p) => {
        const cap = p.compare_at_price_cents || 0;
        return cap > 0 && p.price_cents > 0 && cap > p.price_cents;
      }).slice(0, 6);
      setOffers(list);
    }).catch(() => {});
    return () => { alive = false; };
  }, [enabled, isShopRoot]);

  if (!enabled || !isShopRoot || universe !== "jardinerie") return null;

  return (
    <section className="jo-shop-hero" data-testid="jo-shop-hero">
      <div className="jo-shop-hero-inner">
        {/* Headline */}
        <div className="jo-shop-hero-head">
          <div className="jo-section-eyebrow">— Notre boutique en ligne</div>
          <h1 className="jo-shop-title">
            La pépinière, livrée<br />
            ou retirée près de chez vous.
          </h1>
          <p className="jo-shop-sub">
            Plus de 3 000 références sélectionnées sur place. Click &amp; Collect en 2h, ou livraison à domicile dans un rayon de 40 km autour d'Istres.
          </p>
          <div className="jo-shop-pills">
            <span className="jo-shop-pill"><Store className="w-3.5 h-3.5" /> Click &amp; Collect 2h</span>
            <span className="jo-shop-pill"><Truck className="w-3.5 h-3.5" /> Livraison Istres &amp; 40 km</span>
            <span className="jo-shop-pill"><Tag className="w-3.5 h-3.5" /> Garantie reprise</span>
          </div>
        </div>

        {/* Offres du moment carousel */}
        {offers.length > 0 && (
          <div className="jo-shop-offers" data-testid="jo-shop-offers">
            <div className="jo-shop-offers-head">
              <h2 className="jo-shop-offers-title">
                <Flame className="w-5 h-5" strokeWidth={2.4} />
                Offres du moment
              </h2>
              <span className="jo-shop-offers-count">{offers.length} produits en promo</span>
            </div>
            <div className="jo-shop-offers-grid">
              {offers.map((p) => {
                const off = p.compare_at_price_cents - p.price_cents;
                const pct = Math.round((off / p.compare_at_price_cents) * 100);
                return (
                  <Link key={p.slug || p.id} to={`/shop/${p.slug}`} className="jo-offer-card" data-testid={`jo-offer-${p.slug}`}>
                    <div className="jo-offer-img">
                      {p.featured_image && <img src={p.featured_image} alt={p.name} loading="lazy" />}
                      <span className="jo-offer-badge">-{pct}%</span>
                    </div>
                    <div className="jo-offer-body">
                      <div className="jo-offer-name">{p.name}</div>
                      <div className="jo-offer-prices">
                        <span className="jo-offer-old">{(p.compare_at_price_cents / 100).toFixed(2)} €</span>
                        <span className="jo-offer-new">{(p.price_cents / 100).toFixed(2)} €</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="jo-shop-offers-cta">
              <Link to="/shop?on_sale=1" className="jo-btn-primary">
                Voir toutes les promos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
